import { Plus } from 'lucide-react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormActions, FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { knownQcPaths, qcFieldPath, toQcField } from '../api/mappers';
import {
  emptyQcField,
  MAX_QC_FIELDS,
  qcFormSchema,
  type QcFieldFormValues,
  type QcFormInput,
  type QcFormValues,
} from '../schemas/qc-form-schema';

import { QcFieldCard } from './qc-field-card';
import { QcFormPreview } from './qc-form-preview';

export interface QcChecklistFormProps {
  defaultValues: QcFormInput;
  isSubmitting: boolean;
  submitLabel: string;
  readOnly?: boolean;
  onSubmit: (values: QcFormValues) => Promise<unknown>;
}

function LivePreview() {
  const fields = useWatch<QcFormInput, 'fields'>({ name: 'fields' });
  return <QcFormPreview fields={(fields as QcFieldFormValues[]).map(toQcField)} />;
}

/** Questions on the left, the form as staff will see it on the right, fed by the same values. */
export function QcChecklistForm({
  defaultValues,
  isSubmitting,
  submitLabel,
  readOnly = false,
  onSubmit,
}: QcChecklistFormProps) {
  const { t } = useTranslation('workflows');
  const message = useErrorMessage();
  const form = useForm<QcFormInput, unknown, QcFormValues>({
    resolver: useI18nResolver(qcFormSchema),
    defaultValues,
  });
  const { fields, append, move, remove } = useFieldArray({ control: form.control, name: 'fields' });
  const rootError = form.formState.errors.root?.server?.message;
  const fieldsError =
    form.formState.errors.fields?.message ?? form.formState.errors.fields?.root?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
    } catch (error) {
      const applied = applyFieldErrors(error, form.setError, {
        mapField: qcFieldPath,
        knownFields: knownQcPaths(form.getValues()),
      });
      if (!applied) form.setError('root.server', { type: 'server', message: message(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: isSubmitting, disabled: readOnly });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate>
        <FormLayout
          asideWidth="wide"
          aside={
            <FormSection
              title={t('qc.previewTitle')}
              description={t('qc.previewDescription')}
              tour="qc.editor.preview"
            >
              <LivePreview />
            </FormSection>
          }
        >
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection title={t('qc.name')} tour="qc.editor.name">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">{t('qc.name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={120}
                      placeholder={t('qc.namePlaceholder')}
                      disabled={readOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <FormSection
            title={t('qc.fieldsTitle')}
            description={t('qc.fieldsDescription')}
            tour="qc.editor.questions"
          >
            <ol className="flex flex-col gap-3" aria-label={t('qc.fieldsTitle')}>
              {fields.map((item, index) => (
                <QcFieldCard
                  key={item.id}
                  index={index}
                  total={fields.length}
                  onMove={(to) => move(index, to)}
                  onRemove={() => remove(index)}
                  disabled={readOnly}
                />
              ))}
            </ol>
            {fieldsError ? (
              <p role="alert" className="text-destructive text-sm">
                {fieldsError}
              </p>
            ) : null}
            {readOnly ? null : (
              <Button
                type="button"
                variant="outline"
                className="self-start"
                data-tour="qc.editor.add-question"
                disabled={fields.length >= MAX_QC_FIELDS}
                onClick={() => append(emptyQcField())}
              >
                <Plus aria-hidden />
                {t('qc.addField')}
              </Button>
            )}
          </FormSection>
          {readOnly ? null : (
            <FormActions
              isSubmitting={isSubmitting}
              isDirty={form.formState.isDirty}
              submitLabel={submitLabel}
              tour="qc.editor.save"
              sticky={false}
            />
          )}
        </FormLayout>
      </form>
    </Form>
  );
}
